import React, { useMemo } from 'react'
import './account-table.scss'
import { Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { IAccountData, accountPaymentMap } from '../account-checking'
import { useLocalStorageState, useUpdateEffect } from 'ahooks'
import useBemClass from '@/utils/hooks/useBEMClass'
import { showLayer } from '@/utils/layer'
import AccountDetail from '../account-detail/account-detail'
import { formatPrice } from '@/utils'
import { Row } from '@/components/layout/layout'
import Iconfont from '@/components/iconfont/iconfont'
import SortContainer from '../sort-container/sort-container'
import { accountFilterStore } from '../account-filter/filter-store'
import { observer } from 'mobx-react'

interface AccountTableProps {
    dataList: ITableData[]
    loading: boolean
    height: number
    customPaymentData: Array<{
        id: number
        name: string
    }>
}

interface ITableData extends IAccountData {
    from_date: string
    to_date: string
    type: number
}

const AccountTable: React.FC<AccountTableProps> = observer(props => {
    const cls = useBemClass('account-table')
    const [sortData, setSortData] = useLocalStorageState<ColumnsType<ITableData>>(
        `ACCOUNT_SORT_COLUMN_${localStorage.getItem('shopId')}`,
        [],
    )

    useUpdateEffect(() => {
        if (!sortData?.length) {
            const paymentData = Object.keys(accountPaymentMap).map(a => ({
                id: +a,
                name: accountPaymentMap[a],
            }))
            setSortData(val => {
                return [...paymentData, ...props.customPaymentData].map(p => ({
                    title: p.name,
                    width: [4, 5].includes(p.id) ? 160 : 130,
                    dataIndex: `payment:${p.id}`,
                    checked: true,
                }))
            })
        } else {
            const allTitle = sortData.map(s => s.title)
            const isIncludesCustonPaymentData = props.customPaymentData.every(custom => allTitle.includes(custom.name))
            if (!isIncludesCustonPaymentData) {
                const free = props.customPaymentData.filter(custom => !allTitle.includes(custom.name))

                setSortData(val => {
                    return [
                        ...val,
                        ...free.map(p => ({
                            title: p.name,
                            width: 130,
                            dataIndex: `payment:${p.id}`,
                            checked: true,
                        })),
                    ]
                })
            }
        }
    }, [props.customPaymentData])

    const columns: ColumnsType<ITableData> = useMemo(() => {
        return [
            {
                title: '日期',
                width: 120,
                dataIndex: 'date',
                fixed: 'left',
            },
            ...sortData
                // @ts-ignore
                ?.filter(s => (typeof s.checked === undefined ? true : s.checked))
                .map(s => ({
                    ...s,
                    render: (val: any, record: ITableData) => {
                        const amount = !record.type
                            ? val?.amount
                            : record.type === 1
                            ? val?.cashierAmount
                            : val?.mallAmount

                        return <div data-margin-left-14>{val?.id === -2 ? amount : formatPrice(amount)}</div>
                    },
                    // @ts-ignore
                    sorter: (a, b) => a[s.dataIndex].amount - b[s.dataIndex].amount,
                })),
            {
                title: (
                    <Row data-align-items-center data-justify-content-space-between>
                        <div>操作</div>
                        <div
                            data-cursor-pointer
                            onClick={() => {
                                showLayer(
                                    <SortContainer
                                        // @ts-ignore
                                        sortData={
                                            sortData?.map(s => ({
                                                name: s.title,
                                                // @ts-ignore
                                                id: s.dataIndex,
                                                // @ts-ignore
                                                checked: s.checked,
                                            })) || []
                                        }
                                        onSortEnd={data => {
                                            setSortData(
                                                data.map(d => ({
                                                    title: d.name,
                                                    width: [`payment:4`, `payment:5`].includes(d.id) ? 160 : 130,
                                                    dataIndex: d.id,
                                                    checked: d.checked,
                                                })),
                                            )
                                        }}
                                    />,
                                )
                            }}
                        >
                            <Iconfont size={16} name="iconshezhi2" />
                        </div>
                    </Row>
                ),
                width: 90,
                align: 'left',
                fixed: 'right',
                render: (val, record) =>
                    record.type !== 2 ? (
                        <div
                            className="action"
                            onClick={() => {
                                record.type && showLayer(<AccountDetail date={[record.from_date, record.to_date]} />)
                            }}
                        >
                            {record.type ? '详情' : '展开'}
                        </div>
                    ) : (
                        '-'
                    ),
            },
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortData])

    const formatDataList = useMemo(() => {
        return props.dataList.map(data => {
            const tmp = {
                ...data,
                type: 0,
                children: [],
            }

            if (accountFilterStore.filterQuery.scenes?.includes(0)) {
                ;(tmp.children as any[]).push({
                    ...data,
                    date: '收银台',
                    type: 1,
                })
            }
            if (accountFilterStore.filterQuery.scenes?.includes(1)) {
                ;(tmp.children as any[]).push({
                    ...data,
                    date: '微商城',
                    type: 2,
                })
            }

            return tmp
        })
    }, [props.dataList])

    return (
        <div
            className={cls()}
            style={{
                marginBottom: accountFilterStore.filterText ? '100px' : '60px',
            }}
        >
            <Table
                rowKey={record => record.date}
                bordered
                loading={props.loading}
                pagination={false}
                columns={columns}
                dataSource={formatDataList}
                expandRowByClick
                sticky
                scroll={{ x: 'calc(100% - 120px)', y: props.height - 50 }}
            />
        </div>
    )
})

export default AccountTable
